import { useEffect, useRef, useState } from "react";

// Plays a looping ambience track from a URL (uploaded file or external URL).
// Starts only when `enabled` (user gesture) and gently fades in/out. It also
// automatically "ducks" (pauses) whenever ANY <video> on the page is playing —
// e.g. Vani videos — and resumes once every video is paused/ended, so the
// ambience never competes with video sound.
export const AudioTrack = ({ src, enabled, maxVolume = 0.7 }) => {
  const ref = useRef(null);
  const [ducked, setDucked] = useState(false);

  // Watch every video element globally (capture phase, since media events
  // don't bubble) and also honour explicit "duck" requests fired by inline
  // players (e.g. the Vani video lightbox, which is a cross-origin iframe and
  // can't emit native media events). Duck while a video plays; resume after.
  useEffect(() => {
    let manualDuck = false;
    const anyVideoPlaying = () =>
      Array.from(document.querySelectorAll("video")).some(
        (v) => !v.paused && !v.ended
      );
    const recompute = () => setDucked(manualDuck || anyVideoPlaying());
    const onVideoEvt = (e) => {
      if (e && e.target && e.target.tagName === "VIDEO") recompute();
    };
    const onManualDuck = (e) => {
      manualDuck = !!(e.detail && e.detail.on);
      recompute();
    };
    document.addEventListener("play", onVideoEvt, true);
    document.addEventListener("pause", onVideoEvt, true);
    document.addEventListener("ended", onVideoEvt, true);
    window.addEventListener("tkh-audio-duck", onManualDuck);
    return () => {
      document.removeEventListener("play", onVideoEvt, true);
      document.removeEventListener("pause", onVideoEvt, true);
      document.removeEventListener("ended", onVideoEvt, true);
      window.removeEventListener("tkh-audio-duck", onManualDuck);
    };
  }, []);

  useEffect(() => {
    const a = ref.current;
    if (!a || !src) return;
    let raf;
    const active = enabled && !ducked;
    if (active) {
      if (a.paused) a.volume = 0; // fade in from silence on (re)start
      a.play().catch(() => {});
      const up = () => {
        a.volume = Math.min(maxVolume, a.volume + 0.04);
        if (a.volume < maxVolume) raf = setTimeout(up, 70);
      };
      up();
    } else {
      const down = () => {
        a.volume = Math.max(0, a.volume - 0.06);
        if (a.volume > 0) raf = setTimeout(down, 60);
        else a.pause();
      };
      down();
    }
    return () => clearTimeout(raf);
  }, [enabled, ducked, src, maxVolume]);

  if (!src) return null;
  return <audio ref={ref} src={src} loop preload="auto" data-testid="audio-track" />;
};
